import { FONT_ALLOWLIST } from '../schemas/theme.js';
import { contrastRatio, parseHex } from './contrast.js';
/**
 * How an event's four chosen colours become every colour on a page.
 *
 * This module is the SINGLE SOURCE for that. It lives here, rather than in the
 * attendee site where it was written, because two surfaces have to agree on it
 * or the product lies to the organiser: the admin console's form preview
 * promises "what attendees see", and the attendee page is what they actually
 * see. Those were independent for as long as the preview was hand-styled — it
 * drew the console's own serif over white tiles at the console's own measure,
 * while the attendee page drew the event's fonts over derived tiles at 34rem,
 * and no test could notice because each was internally consistent.
 *
 * The rule the whole file exists to enforce: **no large area is painted a raw
 * theme colour.** The page is `background`; the card and the controls are
 * derived from it. The organiser's colours are spent where they are an accent —
 * the submit button, a selection, a focus ring — and carried as hue inside every
 * derived tint. That is what makes a page's quality independent of a palette's,
 * which matters because nobody reviews these palettes and the console never
 * refuses one (`contrast.ts`: contrast is advice, never a gate).
 *
 * Platform-neutral on purpose: plain colour maths returning hex strings, with no
 * CSS and no React. The attendee site turns these into `--e67-*` custom
 * properties; the console reads the same values straight into inline styles.
 */
const HEX = /^#[0-9a-fA-F]{6}$/;
const channel = (v) => Math.round(Math.min(255, Math.max(0, v)))
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
/**
 * `fg` laid over `bg` at `alpha`, flattened to an opaque hex.
 *
 * Flattened rather than left as `rgba()` because these values become design
 * tokens, and a translucent token stacked over another translucent token
 * compounds — a "10% tint" chip on a "5% elevated" card would drift a shade
 * every time the markup nests one inside the other.
 */
export const mix = (fg, bg, alpha) => {
    const a = parseHex(fg);
    const b = parseHex(bg);
    if (!a || !b)
        return HEX.test(bg) ? bg.toUpperCase() : '#000000';
    const t = Math.min(1, Math.max(0, alpha));
    return `#${channel(b.r + (a.r - b.r) * t)}${channel(b.g + (a.g - b.g) * t)}${channel(b.b + (a.b - b.b) * t)}`;
};
/**
 * Whichever of `a` / `b` reads better on `bg`.
 *
 * Only ever used to *fill in* a token the theme did not supply. A token the
 * organiser did choose is used as chosen, contrast or not.
 */
export const readableOn = (bg, a, b) => {
    if (!parseHex(bg) || !parseHex(a) || !parseHex(b))
        return a;
    return contrastRatio(a, bg) >= contrastRatio(b, bg) ? a : b;
};
/** `hex` as an `rgba()` string, for the one place a token needs to composite. */
export const withAlpha = (hex, a) => {
    const c = parseHex(hex);
    return c ? `rgba(${c.r}, ${c.g}, ${c.b}, ${a})` : `rgba(0, 0, 0, ${a})`;
};
const safeHex = (hex, fallback) => typeof hex === 'string' && HEX.test(hex) ? hex.toUpperCase() : fallback;
/**
 * `fill` pulled toward `bg` until the two are within `maxRatio` of each other.
 *
 * This is the rule that decides how loud a large area is allowed to be, and it
 * is the whole reason a page survives a palette nobody reviewed.
 *
 * A theme gives four colours and no art direction. Painted as large flat fills,
 * one unlucky token decides how the page reads: an event whose theme set
 * `surface: #A7A9AB` got a mid-grey slab the size of the form, and no amount of
 * work on the fields inside it could fix that. Scaling every palette by a fixed
 * ratio is not the answer either — it would flatten the themes that were already
 * right, and the default theme's white-card-on-warm-grey is exactly that.
 *
 * So the cap is on the DIFFERENCE, not the colour. A surface already sitting
 * close to its background is returned untouched, because the organiser's intent
 * was already quiet. A surface that shouts is walked toward the background until
 * it stops, keeping its hue the whole way. What makes the card a card afterwards
 * is its edge and its shadow, not the size of its colour.
 *
 * Binary search rather than a guessed constant: contrast is not linear in the
 * mix, so the alpha that lands on a given ratio has to be found. Fourteen
 * halvings put it within 1/16384 — deterministic, and far finer than 8-bit
 * colour can express.
 */
export const softenToward = (fill, bg, maxRatio) => {
    if (!parseHex(fill) || !parseHex(bg))
        return fill;
    if (contrastRatio(fill, bg) <= maxRatio)
        return fill.toUpperCase();
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 14; i++) {
        const mid = (lo + hi) / 2;
        if (contrastRatio(mix(fill, bg, mid), bg) > maxRatio)
            hi = mid;
        else
            lo = mid;
    }
    return mix(fill, bg, lo);
};
/**
 * How far the card may sit from the page. Small on purpose.
 *
 * 1.18:1 is under the threshold where a fill reads as a block of colour and
 * still above where it disappears. Both shipped palettes sit under it and are
 * returned untouched — the default light theme at 1.04, the default dark at
 * 1.15 — while the grey that started this is at 1.52 and is pulled down to a
 * tint. It is a design constant, not a legibility one; text contrast is never
 * decided here.
 */
export const CARD_LIFT_MAX = 1.18;
/**
 * The card's fill: `surface` quieted, but never at the cost of its own text.
 *
 * The guard is the whole function. Quieting a fill means walking it toward the
 * page, and for a theme whose card is dark on a light page — `surface: #101418`
 * with `onSurface: #FFFFFF` — that walk ends at a near-white card still being
 * asked to carry white text. The panel would have gone from loud to unreadable,
 * a far worse trade than the one it was making.
 *
 * So the softened fill is taken only when it reads at least as well as the one
 * the organiser chose. That single test sorts both cases without naming either:
 * a committed dark card keeps its commitment, because softening would destroy
 * it; a muddy mid-tone carrying dark text is quieted, because softening makes
 * the text *better*. Nothing here overrules a palette; it declines to make one
 * worse.
 */
export const cardFill = (surface, background, onSurface) => {
    const softened = softenToward(surface, background, CARD_LIFT_MAX);
    return contrastRatio(onSurface, softened) >= contrastRatio(onSurface, surface)
        ? softened
        : safeHex(surface, surface);
};
/**
 * The background a *control* is painted with. Never the card it sits on.
 *
 * A control painted the same colour as its card has no edge, and a form drawn
 * that way reads as one flat slab with some text on it — which is exactly what
 * the `#A7A9AB` event shipped: a grey card holding grey inputs, every field
 * invisible until you clicked in it. A border alone does not rescue it; fields
 * are recognised by their fill first.
 *
 * A step from the CARD toward the ink, which is the one direction that is right
 * in both polarities: on a light theme `onSurface` is dark, so the control
 * recesses; on a dark theme it is light, so it lifts. Neither needs to know
 * which it is in.
 */
export const fieldBg = (card, onSurface) => {
    const step = mix(onSurface, card, 0.05);
    if (step.toUpperCase() !== card.toUpperCase())
        return step;
    // The ink and the card are the same colour. The palette is already broken and
    // this is not the place to overrule it — but a control still has to be
    // findable, and "toward the ink" has nowhere to go, so it steps toward
    // whichever end of the range the card is not sitting at.
    return mix(readableOn(card, '#000000', '#FFFFFF'), card, 0.06);
};
/**
 * The reading measure for a form, in rem.
 *
 * Narrower than a prose measure deliberately: a column of single-line inputs set
 * to the width of a paragraph reads as a spreadsheet, because the eye has to
 * travel a paragraph to get from a label to the end of a one-word answer. Shared
 * so the console's preview is the same shape as the page it is previewing, which
 * it was not — the preview was whatever width the console's sidebar happened to
 * be.
 */
export const FORM_COLUMN_REM = 34;
/**
 * The three corner radii, from the one the organiser set.
 *
 * Shared for the same reason the colours are: a preview drawn at its own radii
 * is a picture of a different form. The clamp is not cosmetic — an unbounded
 * `cornerRadius` on the large radius turns a card into a lozenge.
 */
export const radiusScale = (theme) => {
    const raw = theme.shape?.cornerRadius;
    const n = typeof raw === 'number' && Number.isFinite(raw) ? Math.round(raw) : 12;
    const base = Math.min(48, Math.max(0, n));
    return { base, sm: Math.round(base * 0.55), lg: Math.min(32, Math.round(base * 1.6)) };
};
export const paletteRoles = (tokens) => {
    const surface = safeHex(tokens.surface, '#FFFFFF');
    const background = safeHex(tokens.background, '#FFFFFF');
    const onSurface = safeHex(tokens.onSurface, '#000000');
    const primary = safeHex(tokens.primary, '#000000');
    const onPrimary = safeHex(tokens.onPrimary, readableOn(primary, '#FFFFFF', '#000000'));
    const raised = cardFill(surface, background, onSurface);
    const field = fieldBg(raised, onSurface);
    // Ink for the text that is NOT on a card — the event name, the form's title,
    // the greeting, the footer note. `onSurface` is the colour the organiser chose
    // for text ON THEIR CARD, and using it on the page is only safe while the two
    // are close. They are not always: a theme with a dark card on a light page
    // sets `onSurface: #FFFFFF`, and the page title rendered white on white — the
    // heading simply was not there. Found by rendering a stress palette, not by a
    // test, because every assertion about it passes either way.
    const onBackground = contrastRatio(onSurface, background) >= 4.5
        ? onSurface
        : readableOn(background, '#000000', '#FFFFFF');
    return {
        primary,
        onPrimary,
        primaryHover: mix(readableOn(onPrimary, '#000000', '#FFFFFF'), primary, 0.14),
        secondary: safeHex(tokens.secondary, primary),
        background,
        surface,
        onSurface,
        onBackground,
        mutedOnBackground: mix(onBackground, background, 0.62),
        raised,
        raisedBorder: mix(onSurface, raised, 0.13),
        // Black, and not derived from the palette, because a shadow is not a colour
        // choice — it is less light, and it is less light on a dark theme too.
        // Deriving it from `onSurface` looked principled and was wrong: on a dark
        // palette the ink is nearly white, so every card wore a white halo. On a
        // dark theme this is close to invisible and the border does the work, which
        // is the correct outcome rather than a gap.
        cardShadow: `0 1px 2px ${withAlpha('#000000', 0.05)}, 0 12px 32px -14px ${withAlpha('#000000', 0.18)}`,
        field,
        fieldBorder: mix(onSurface, field, 0.28),
        fieldMuted: mix(onSurface, field, 0.55),
        fieldLine: mix(onSurface, field, 0.12),
        // The chosen row of a choice stack. Tinted from the FIELD toward primary
        // rather than reusing `soft`, which is mixed against the surface: on an
        // event whose card and controls are different colours — the normal case — a
        // surface tint sitting on a control reads as a smudge rather than a
        // selection.
        fieldOn: mix(primary, field, 0.14),
        // Mixed against the CARD, not `surface`: this is the colour of help text and
        // captions, and all of them sit on a card whose fill may have moved.
        muted: mix(onSurface, raised, 0.62),
        outline: mix(onSurface, raised, 0.18),
        hairline: mix(onBackground, background, 0.1),
        soft: mix(primary, surface, 0.09),
        elevate: mix(onSurface, surface, 0.05),
        focus: mix(primary, background, 0.55),
        error: safeHex(tokens.error, '#B3261E'),
        success: safeHex(tokens.success, mix(primary, surface, 0.85)),
    };
};
// ---- typography -----------------------------------------------------------
const FONTS = new Set(FONT_ALLOWLIST);
/**
 * A font family name that is safe to interpolate into CSS.
 *
 * The allowlist is the whole check. It is not paranoia about the schema — the
 * theme is `.strict()` and the families are a zod enum — it is that this string
 * is written into a `<style>` element, and the one place a themed value reaches
 * raw CSS is the one place that must not depend on a validator three services
 * away still being there tomorrow.
 */
export const safeFont = (family, fallback) => typeof family === 'string' && FONTS.has(family) ? family : fallback;
/**
 * The fallback stacks, shared so a preview and the page it previews resolve the
 * same family when the webfont has not landed yet.
 */
export const HEADING_FALLBACK_STACK = 'ui-serif, Georgia, serif';
export const BODY_FALLBACK_STACK = 'ui-sans-serif, system-ui, sans-serif';
/** The two `font-family` values a theme resolves to, fallbacks included. */
export const fontStacks = (theme) => ({
    heading: `'${safeFont(theme.typography?.headingFamily, 'Fraunces')}', ${HEADING_FALLBACK_STACK}`,
    body: `'${safeFont(theme.typography?.bodyFamily, 'Inter')}', ${BODY_FALLBACK_STACK}`,
});
const WEIGHTS = '400;500;600;700';
/**
 * The Google Fonts stylesheet URL for a theme's two families, or null when the
 * theme asks for nothing beyond the system stack.
 *
 * Families are put through the theme allowlist first and URL-encoded second, so
 * a family name can only ever become a `family=` value — never another query
 * parameter, and never a different host.
 */
export const fontHref = (theme) => {
    const families = [
        safeFont(theme.typography?.headingFamily, 'Fraunces'),
        safeFont(theme.typography?.bodyFamily, 'Inter'),
    ];
    const unique = [...new Set(families)];
    if (unique.length === 0)
        return null;
    // The family is encoded because it is the only part that varies; WEIGHTS is
    // a constant in this file and Google Fonts wants its separators literal —
    // encoding them to %3B works only because the far end decodes, which is a
    // thing to rely on by accident rather than on purpose.
    const query = unique.map((f) => `family=${encodeURIComponent(f)}:wght@${WEIGHTS}`).join('&');
    return `https://fonts.googleapis.com/css2?${query}&display=swap`;
};
/**
 * The id both web packages give the one `<link>` that loads the event's fonts.
 *
 * The element itself is installed by each package rather than here: this file is
 * also on the backend's dependency path and must not reference the DOM. What
 * has to agree across the seam is the URL and the id, and both are here — the
 * twelve lines of `document.createElement` around them carry no logic that can
 * drift.
 */
export const FONT_LINK_ID = 'e67-brand-fonts';
//# sourceMappingURL=palette_roles.js.map